import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Plus, Trash2, Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";

const inputCls = "w-full px-3 py-2 border border-zinc-200 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-500 text-sm";

const AdminUsers = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [creating, setCreating] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/users").then((r) => setItems(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const submit = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/admin/users", form);
      toast.success("Admin created");
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this admin?")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div data-testid="admin-users">
      <h1 className="font-display text-3xl font-black tracking-tight">Admin users</h1>
      <p className="text-sm text-zinc-500 mt-1">{items.length} total · You're signed in as <b>{user?.email}</b></p>

      <section className="mt-8 bg-white border border-zinc-200 rounded-md p-6">
        <div className="text-xs uppercase tracking-widest font-bold text-zinc-500 mb-4 inline-flex items-center gap-2"><UserPlus size={14} /> Add new admin</div>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input className={inputCls} placeholder="Name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required data-testid="admin-user-name" />
          <input className={inputCls} type="email" placeholder="Email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} required data-testid="admin-user-email" />
          <input className={inputCls} type="password" placeholder="Password (min 6)" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} required minLength={6} data-testid="admin-user-password" />
          <button type="submit" disabled={creating} data-testid="admin-user-create" className="bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-md inline-flex items-center justify-center gap-2 disabled:opacity-50">
            {creating ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Create
          </button>
        </form>
      </section>

      <div className="mt-6 bg-white border border-zinc-200 rounded-md">
        {loading ? (
          <div className="p-10 text-center text-zinc-500 text-sm">Loading…</div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {items.map((u) => (
              <div key={u.id} className="px-6 py-4 flex items-center justify-between gap-4">
                <div>
                  <div className="font-semibold text-zinc-900">{u.name} <span className="text-zinc-400 font-normal">· {u.email}</span></div>
                  <div className="text-xs text-zinc-500 mt-1">{u.role} · created {new Date(u.created_at).toLocaleDateString()}</div>
                </div>
                {u.id !== user?.id && (
                  <button onClick={() => remove(u.id)} className="p-2 rounded-md hover:bg-red-50 text-red-600" data-testid={`admin-user-delete-${u.id}`}>
                    <Trash2 size={16} />
                  </button>
                )}
                {u.id === user?.id && <span className="text-xs text-zinc-400">(you)</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsers;
